/**
 * ClientZone ticket detail view (JWT-scoped conversation + reply).
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { formatDateTime } from "utils/dateFormatter";
import { getDepartmentLabel } from "utils/departmentUtils";
import { fetchClientzoneTicket, replyClientzoneTicket } from "utils/clientzonePortalApi";

function renderHtmlContent(htmlContent) {
  if (!htmlContent || typeof htmlContent !== "string") {
    return { __html: "" };
  }
  return { __html: htmlContent };
}

function statusChipColor(label) {
  if (label === "Open") return "error";
  if (label === "Closed") return "success";
  if (label === "Done") return "warning";
  return "default";
}

export default function ClientTicketViewLayout({ clientAccessToken }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || "/profile";
  const returnState =
    location.state?.activeCustomerTab != null
      ? { activeCustomerTab: location.state.activeCustomerTab }
      : { activeCustomerTab: 3 };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyError, setReplyError] = useState(null);
  const [replySaving, setReplySaving] = useState(false);

  const loadTicket = useCallback(async () => {
    const id = Number(ticketId);
    if (!clientAccessToken || !Number.isFinite(id) || id < 1) {
      setError("Invalid ticket request.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchClientzoneTicket(id, clientAccessToken);
      setPayload(data);
    } catch (err) {
      setError(err?.message || "Unable to load this ticket.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId, clientAccessToken]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const ticket = payload?.ticket;
  const isClosed = String(ticket?.status_label || "").toLowerCase() === "closed";

  const handleReply = async () => {
    const id = Number(ticketId);
    const message = String(replyMessage || "").trim();
    if (!message) {
      setReplyError("Please enter a reply message.");
      return;
    }
    if (!clientAccessToken || !Number.isFinite(id) || id < 1) {
      setReplyError("Invalid ticket request.");
      return;
    }

    setReplySaving(true);
    setReplyError(null);
    try {
      const data = await replyClientzoneTicket(id, { message }, clientAccessToken);
      setPayload(data);
      setReplyMessage("");
    } catch (err) {
      setReplyError(err?.message || "Unable to send reply.");
    } finally {
      setReplySaving(false);
    }
  };

  return (
    <MDBox py={2} px={{ xs: 1, sm: 2 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          onClick={() => navigate(returnUrl, { state: returnState })}
        >
          <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
          Back
        </MDButton>
        {ticket?.ticket_id ? (
          <MDTypography variant="button" color="text">
            Ticket #{ticket.ticket_id}
          </MDTypography>
        ) : null}
      </MDBox>

      <Card>
        <CardContent>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </MDBox>
          ) : null}

          {!loading && error ? (
            <MDBox p={2}>
              <MDTypography variant="subtitle1" color="error" gutterBottom>
                We couldn&apos;t load this ticket
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {error}
              </MDTypography>
            </MDBox>
          ) : null}

          {!loading && !error && ticket ? (
            <>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
                <Chip
                  label={ticket.status_label || "—"}
                  color={statusChipColor(ticket.status_label)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Priority: ${ticket.priority_label || "—"}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={getDepartmentLabel(ticket.department) || ticket.department || "—"}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Reference
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
                  >
                    {ticket.ticket_code}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Account
                  </MDTypography>
                  <MDTypography variant="body2">{ticket.customer_display}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Opened
                  </MDTypography>
                  <MDTypography variant="body2">{formatDateTime(ticket.create_datetime)}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Last update
                  </MDTypography>
                  <MDTypography variant="body2">{formatDateTime(ticket.update_datetime)}</MDTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTypography variant="caption" color="text" fontWeight="bold">
                    Resolved
                  </MDTypography>
                  <MDTypography variant="body2">
                    {ticket.resolve_datetime ? formatDateTime(ticket.resolve_datetime) : "—"}
                  </MDTypography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Conversation
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Newest messages appear first
              </MDTypography>

              {!isClosed ? (
                <MDBox mb={3}>
                  <TextField
                    label="Your reply"
                    value={replyMessage}
                    onChange={(event) => setReplyMessage(event.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={replySaving}
                    placeholder="Type your reply..."
                  />
                  {replyError ? (
                    <MDTypography color="error" variant="caption" display="block" mt={1}>
                      {replyError}
                    </MDTypography>
                  ) : null}
                  <MDBox display="flex" justifyContent="flex-end" mt={1.5}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleReply}
                      disabled={replySaving}
                    >
                      {replySaving ? "Sending..." : "Send Reply"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox mb={3}>
                  <MDTypography variant="body2" color="text">
                    This ticket is closed. You can still read the conversation below.
                  </MDTypography>
                </MDBox>
              )}

              {(payload.messages || []).length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No messages have been posted on this ticket yet.
                </MDTypography>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {(payload.messages || []).map((msg, idx) => (
                    <Paper
                      key={`${msg.datetime}-${idx}`}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2 }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1} gap={1}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <MDTypography variant="button" fontWeight="medium">
                            {msg.sender_label || "Support team"}
                          </MDTypography>
                          {msg.sender_label === "Sector Analysis" ? (
                            <Chip label="System" size="small" color="secondary" variant="outlined" />
                          ) : null}
                          {msg.sender_label === "You" ? (
                            msg.is_read ? (
                              <Chip label="Read" size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip label="Unread" size="small" color="warning" />
                            )
                          ) : null}
                        </Box>
                        <MDTypography variant="caption" color="text">
                          {formatDateTime(msg.datetime)}
                        </MDTypography>
                      </Box>
                      <Box
                        sx={{
                          typography: "body2",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          "& img": { maxWidth: "100%", height: "auto" },
                          "& p": { margin: "0.35rem 0" },
                        }}
                      >
                        <span dangerouslySetInnerHTML={renderHtmlContent(msg.message)} />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>
    </MDBox>
  );
}

ClientTicketViewLayout.propTypes = {
  clientAccessToken: PropTypes.string.isRequired,
};
